class SliderComponent extends HTMLElement {
  constructor() {
    super()
    this.slider = this.querySelector("slider-slides")
    this.prevBtn = this.querySelector('button[name="previous"]')
    this.nextBtn = this.querySelector('button[name="next"]')
    // Updated selector to match new wrapper div
    this.sliderItems = this.querySelectorAll(".slider-slide")
    this.thumbnails = this.querySelectorAll("slider-thumbnails > button")

    this.thumbnailContainer = this.querySelector("slider-thumbnails")

    this.activeIndex = 0

    this.isInitialized = false

    if (!this.slider) return
    this.init()
  }

  init() {
    if (this.prevBtn && this.nextBtn) {
      this.prevBtn.addEventListener("click", this.onButtonClick.bind(this))
      this.nextBtn.addEventListener("click", this.onButtonClick.bind(this))
      this.slider.addEventListener("scroll", this.updateButtonStates.bind(this))
    }

    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener("click", (e) => {
        e.preventDefault()
        const targetIndex = thumbnail.dataset.index
        const targetSlide = this.slider.querySelector(
          `[data-index="${targetIndex}"]`
        )
        if (targetSlide) {
          this.slider.scrollTo({
            left: targetSlide.offsetLeft,
            behavior: this.getScrollBehavior(),
          })
        }
      })
    })

    const observerOptions = {
      root: this.slider,
      threshold: 0.5,
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const slideIndex = entry.target.dataset.index
          this.activeIndex = slideIndex
          this.updateActiveThumbnail(slideIndex)
        }
      })
    }, observerOptions)

    this.sliderItems.forEach((slide) => this.observer.observe(slide))
  }

  getScrollBehavior() {
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
    return motionQuery.matches ? "auto" : "smooth"
  }

  updateActiveThumbnail(index) {
    // 1. Identify the 'Old' active bullet to see if it affects our position
    // We find the button that CURRENTLY has aria-current="true" before we update it
    if (this.isInitialized == false) {
      this.isInitialized = true
      return
    }

    const oldActiveThumb = Array.from(this.thumbnails).find(
      (t) => t.getAttribute("aria-current") === "true"
    )
    const oldIndex = oldActiveThumb ? Number(oldActiveThumb.dataset.index) : -1

    // First pass (synchronous): deactivate all thumbnails.
    // This triggers the shrink transition on the old active bullet.
    this.thumbnails.forEach((thumb) => {
      thumb.setAttribute("aria-selected", "false")
      thumb.removeAttribute("aria-current")
    })

    // Second pass (next frame): activate the new thumbnail.
    // Separating into a new frame prevents Safari from running
    // concurrent width transitions in the same flex container.
    requestAnimationFrame(() => {
      const targetThumb = Array.from(this.thumbnails).find(
        (t) => Number(t.dataset.index) === Number(index)
      )
      if (!targetThumb) return

      targetThumb.setAttribute("aria-selected", "true")
      targetThumb.setAttribute("aria-current", "true")

      if (this.thumbnailContainer) {
        const currentWidth = targetThumb.offsetWidth
        const finalWidth = currentWidth * 6
        const expansionDelta = finalWidth - currentWidth

        let targetLeft = targetThumb.offsetLeft

        if (oldIndex !== -1 && oldIndex < Number(index)) {
          targetLeft -= expansionDelta
        }

        const containerWidth = this.thumbnailContainer.offsetWidth
        const scrollPos = targetLeft + finalWidth / 2 - containerWidth / 2

        this.thumbnailContainer.scrollTo({
          left: scrollPos,
          behavior: this.getScrollBehavior(),
        })
      }
    })
  }

  onButtonClick(event) {
    event.preventDefault()

    const isNext = event.currentTarget.name === "next"

    if (isNext && this.activeIndex < this.sliderItems.length - 1)
      this.activeIndex++
    else if (!isNext && this.activeIndex > 0) this.activeIndex--
    else return

    this.slider.scrollTo({
      left: this.sliderItems[this.activeIndex].offsetLeft,
      behavior: this.getScrollBehavior(),
    })
  }

  updateButtonStates() {
    const scrollTop = this.slider.scrollLeft
    const scrollWidth = this.slider.scrollWidth
    const clientWidth = this.slider.clientWidth

    if (this.prevBtn) {
      this.prevBtn.disabled = scrollTop <= 1
    }

    if (this.nextBtn) {
      this.nextBtn.disabled =
        Math.ceil(scrollTop + clientWidth) >= scrollWidth - 2
    }
  }

  /**
   * Re-query slides and thumbnails after the DOM changes
   * (e.g. a variant slide was prepended or removed).
   */
  refresh() {
    if (this.observer) {
      this.sliderItems.forEach((slide) => this.observer.unobserve(slide))
    }

    this.sliderItems = this.querySelectorAll(".slider-slide")
    this.thumbnails = this.querySelectorAll("slider-thumbnails > button")
    this.thumbnailContainer = this.querySelector("slider-thumbnails")

    if (this.observer) {
      this.sliderItems.forEach((slide) => this.observer.observe(slide))
    }

    this.thumbnails.forEach((thumbnail) => {
      const fresh = thumbnail.cloneNode(true)
      thumbnail.parentNode.replaceChild(fresh, thumbnail)
      fresh.addEventListener("click", (e) => {
        e.preventDefault()
        const targetSlide = this.slider.querySelector(
          `[data-index="${fresh.dataset.index}"]`
        )
        if (targetSlide) {
          this.slider.scrollTo({
            left: targetSlide.offsetLeft,
            behavior: this.getScrollBehavior(),
          })
        }
      })
    })
    this.thumbnails = this.querySelectorAll("slider-thumbnails > button")

    this.activeIndex = 0
    this.updateButtonStates()
  }
}

customElements.define("slider-component", SliderComponent)
